import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import PropTypes from "prop-types";
import planService from "services/plans.js";
import logger from "utils/logger";

export default function CardPlanForm({ plan, isOpen, onSave, onCancel }) {
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    description: '',
    is_active: true,
  });

  const [configurations, setConfigurations] = useState([]);
  const [newConfig, setNewConfig] = useState({ key: '', value: '' });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  // Load plan data when editing
  useEffect(() => {
    if (isOpen) {
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';

      if (plan) {
        setFormData({
          name: plan.name || '',
          slug: plan.slug || '',
          description: plan.description || '',
          is_active: plan.is_active !== undefined ? plan.is_active : true,
        });

        // Convert configurations object to array
        const configArray = plan.configurations
          ? Object.entries(plan.configurations).map(([key, value]) => ({ key, value }))
          : [];
        setConfigurations(configArray);
      } else {
        // Reset form for new plan
        setFormData({
          name: '',
          slug: '',
          description: '',
          is_active: true,
        });
        setConfigurations([]);
      }
      setErrors({});
      setNewConfig({ key: '', value: '' });
    } else {
      // Restore body scroll when modal is closed
      document.body.style.overflow = 'unset';
    }

    // Cleanup function
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [plan, isOpen]);

  // Handle ESC key press to close modal
  useEffect(() => {
    const handleEscKey = (event) => {
      if (event.key === 'Escape' && isOpen) {
        onCancel();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscKey);
    }

    return () => {
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [isOpen, onCancel]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  // Auto-generate slug from name
  const handleNameChange = (e) => {
    const name = e.target.value;
    const slug = name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
    setFormData({
      ...formData,
      name: name,
      slug: slug
    });
  };

  const handleAddConfiguration = () => {
    if (newConfig.key && newConfig.value) {
      // Check if key already exists
      const exists = configurations.find(c => c.key === newConfig.key);
      if (exists) {
        alert('Esta configuración ya existe. Edítala o usa otra clave.');
        return;
      }

      setConfigurations([...configurations, { ...newConfig }]);
      setNewConfig({ key: '', value: '' });
    }
  };

  const handleRemoveConfiguration = (index) => {
    setConfigurations(configurations.filter((_, i) => i !== index));
  };

  const handleUpdateConfiguration = (index, field, value) => {
    const updated = [...configurations];
    updated[index][field] = value;
    setConfigurations(updated);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrors({});

    try {
      const submitData = {
        ...formData,
        configurations: configurations.map(config => ({
          key: config.key,
          value: config.value
        }))
      };

      let result;
      if (plan) {
        result = await planService.updatePlan(plan.id, submitData);
      } else {
        result = await planService.createPlan(submitData);
      }

      onSave(result);
    } catch (error) {
      logger.error('Error saving plan:', error);
      if (error.response?.data?.errors) {
        setErrors(error.response.data.errors);
      } else {
        alert(error.response?.data?.message || 'Error al guardar el plan');
      }
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return createPortal(
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        zIndex: 99999,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '1rem'
      }}
      onClick={onCancel}
    >
      <div
        style={{
          backgroundColor: 'white',
          borderRadius: '8px',
          width: '100%',
          maxWidth: '800px',
          maxHeight: '90vh',
          overflowY: 'auto',
          zIndex: 100000,
          boxShadow: '0 10px 25px rgba(0, 0, 0, 0.5)'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        <form onSubmit={handleSubmit}>
          {/* Header */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '1.5rem 1.5rem 1rem',
            borderBottom: '1px solid #e5e7eb'
          }}>
            <h3 style={{ fontSize: '1.125rem', fontWeight: '600', margin: 0 }}>
              {plan ? 'Editar Plan' : 'Nuevo Plan'}
            </h3>
            <button
              type="button"
              onClick={onCancel}
              style={{
                border: 'none',
                background: 'none',
                fontSize: '1.5rem',
                cursor: 'pointer',
                color: '#6b7280',
                padding: '0.25rem'
              }}
            >
              ×
            </button>
          </div>

          {/* Form Fields */}
          <div style={{ padding: '1.5rem' }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              {/* Name */}
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', marginBottom: '0.5rem' }}>
                  Nombre del Plan *
                </label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleNameChange}
                  required
                  style={{
                    width: '100%',
                    padding: '0.5rem 0.75rem',
                    border: '1px solid #d1d5db',
                    borderRadius: '0.375rem',
                    fontSize: '0.875rem'
                  }}
                  placeholder="FREE, PREMIUM, PRO"
                />
                {errors.name && (
                  <p style={{ marginTop: '0.25rem', fontSize: '0.875rem', color: '#ef4444' }}>{errors.name[0]}</p>
                )}
              </div>

              {/* Slug */}
              <div>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', marginBottom: '0.5rem' }}>
                  Slug (identificador único) *
                </label>
                <input
                  type="text"
                  name="slug"
                  value={formData.slug}
                  onChange={handleInputChange}
                  required
                  style={{
                    width: '100%',
                    padding: '0.5rem 0.75rem',
                    border: '1px solid #d1d5db',
                    borderRadius: '0.375rem',
                    fontSize: '0.875rem'
                  }}
                  placeholder="free, premium, pro"
                />
                {errors.slug && (
                  <p style={{ marginTop: '0.25rem', fontSize: '0.875rem', color: '#ef4444' }}>{errors.slug[0]}</p>
                )}
              </div>

              {/* Is Active */}
              <div style={{ display: 'flex', alignItems: 'center', paddingTop: '2rem' }}>
                <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    name="is_active"
                    checked={formData.is_active}
                    onChange={handleInputChange}
                    style={{ marginRight: '0.5rem' }}
                  />
                  <span style={{ fontSize: '0.875rem' }}>Plan activo</span>
                </label>
              </div>

              {/* Description */}
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={{ display: 'block', fontSize: '0.875rem', fontWeight: '500', marginBottom: '0.5rem' }}>
                  Descripción
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  rows="3"
                  style={{
                    width: '100%',
                    padding: '0.5rem 0.75rem',
                    border: '1px solid #d1d5db',
                    borderRadius: '0.375rem',
                    fontSize: '0.875rem'
                  }}
                  placeholder="Describe las características del plan"
                ></textarea>
                {errors.description && (
                  <p style={{ marginTop: '0.25rem', fontSize: '0.875rem', color: '#ef4444' }}>{errors.description[0]}</p>
                )}
              </div>

              {/* Configurations Section */}
              <div style={{ gridColumn: '1 / -1', marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid #e5e7eb' }}>
                <h4 style={{ fontSize: '1rem', fontWeight: '600', marginBottom: '1rem' }}>
                  Configuraciones del Plan
                </h4>

                {/* Existing Configurations */}
                <div style={{ marginBottom: '1rem' }}>
                  {configurations.map((config, index) => (
                    <div key={index} style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.5rem' }}>
                      <input
                        type="text"
                        value={config.key}
                        onChange={(e) => handleUpdateConfiguration(index, 'key', e.target.value)}
                        style={{
                          flex: 1,
                          padding: '0.5rem 0.75rem',
                          border: '1px solid #d1d5db',
                          borderRadius: '0.375rem',
                          fontSize: '0.875rem'
                        }}
                        placeholder="Clave (ej: price, max_enabled_projects)"
                      />
                      <input
                        type="text"
                        value={config.value}
                        onChange={(e) => handleUpdateConfiguration(index, 'value', e.target.value)}
                        style={{
                          flex: 1,
                          padding: '0.5rem 0.75rem',
                          border: '1px solid #d1d5db',
                          borderRadius: '0.375rem',
                          fontSize: '0.875rem'
                        }}
                        placeholder="Valor (ej: 29.99, 10, -1 para ilimitado)"
                      />
                      <button
                        type="button"
                        onClick={() => handleRemoveConfiguration(index)}
                        style={{
                          padding: '0.5rem 0.75rem',
                          backgroundColor: '#ef4444',
                          color: 'white',
                          border: 'none',
                          borderRadius: '0.375rem',
                          cursor: 'pointer'
                        }}
                      >
                        <i className="fas fa-trash"></i>
                      </button>
                    </div>
                  ))}
                </div>

                {/* Add New Configuration */}
                <div style={{ backgroundColor: '#f9fafb', padding: '1rem', borderRadius: '0.375rem' }}>
                  <p style={{ fontSize: '0.875rem', marginBottom: '0.5rem', fontWeight: '500' }}>
                    Agregar nueva configuración
                  </p>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <input
                      type="text"
                      value={newConfig.key}
                      onChange={(e) => setNewConfig({ ...newConfig, key: e.target.value })}
                      style={{
                        flex: 1,
                        padding: '0.5rem 0.75rem',
                        border: '1px solid #d1d5db',
                        borderRadius: '0.375rem',
                        fontSize: '0.875rem'
                      }}
                      placeholder="Clave (ej: price)"
                    />
                    <input
                      type="text"
                      value={newConfig.value}
                      onChange={(e) => setNewConfig({ ...newConfig, value: e.target.value })}
                      style={{
                        flex: 1,
                        padding: '0.5rem 0.75rem',
                        border: '1px solid #d1d5db',
                        borderRadius: '0.375rem',
                        fontSize: '0.875rem'
                      }}
                      placeholder="Valor (ej: 29.99)"
                    />
                    <button
                      type="button"
                      onClick={handleAddConfiguration}
                      style={{
                        padding: '0.5rem 1rem',
                        backgroundColor: '#10b981',
                        color: 'white',
                        border: 'none',
                        borderRadius: '0.375rem',
                        cursor: 'pointer',
                        fontSize: '0.875rem',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      <i className="fas fa-plus" style={{ marginRight: '0.25rem' }}></i> Agregar
                    </button>
                  </div>
                  <p style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.5rem' }}>
                    Configuraciones comunes: price, max_enabled_projects, max_tasks_per_project, max_agents, support_level
                    <br />
                    Usa -1 para ilimitado
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: '0.75rem',
            padding: '1rem 1.5rem',
            borderTop: '1px solid #e5e7eb',
            backgroundColor: '#f9fafb'
          }}>
            <button
              type="button"
              onClick={onCancel}
              style={{
                padding: '0.5rem 1rem',
                backgroundColor: 'white',
                border: '1px solid #d1d5db',
                borderRadius: '0.375rem',
                cursor: 'pointer',
                fontSize: '0.875rem'
              }}
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading}
              style={{
                padding: '0.5rem 1rem',
                backgroundColor: loading ? '#9ca3af' : '#4f46e5',
                color: 'white',
                border: 'none',
                borderRadius: '0.375rem',
                cursor: loading ? 'not-allowed' : 'pointer',
                fontSize: '0.875rem'
              }}
            >
              {loading ? 'Guardando...' : (plan ? 'Actualizar' : 'Crear Plan')}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
}

CardPlanForm.propTypes = {
  plan: PropTypes.object,
  isOpen: PropTypes.bool.isRequired,
  onSave: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
};
